import { type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOutletContext, useRouteLoaderData } from '@remix-run/react';
import type {
  PodcastEpisodeEmbedInteractionContextType,
  PollInteractionContextType,
  PostTheme,
  TPostContentElement,
  WebTheme,
} from '@shared/dream-components';
import {
  PodcastEpisodeEmbedInteractionContext,
  PollInteractionContext,
  PostContentElement,
} from '@shared/dream-components';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { PostDataContext } from '~/context/PostDataContext';
import type { PostPageContextType } from '~/context/PostPageContext';
import { usePostDataContext } from '~/context/usePostDataContext';
import { usePostPageContext } from '~/context/usePostPageContext';
import useRenderBlueskyEmbeds from '~/hooks/useRenderBlueskyEmbeds';
import useRenderStocktwitsEmbeds from '~/hooks/useRenderStocktwitsEmbeds';
import useRenderTiktokEmbeds from '~/hooks/useRenderTiktokEmbeds';
import useRenderTwitterEmbeds from '~/hooks/useRenderTwitterEmbeds';
import type { OutletContext } from '~/interfaces/outlet_context';
import { isMeteredPaywall } from '~/interfaces/paywall';
import type { RootLoaderData } from '~/root';
import rewriteSrc from '~/utils/rewriteSrc';

import { PostAudio } from '../PostAudio';
import { ChildDecoratorComponent } from './ChildDecoratorComponent';
import { PostContentPaywall } from './PostContentPaywall';
import { PostContentSkeleton } from './PostContentSkeleton';
import { type ProcessorFunction, type ProcessorFunctionOptions, process, processors } from './processors';

interface Props {
  node: TPostContentElement;
  children: ReactNode | ReactNode[];
  webTheme?: WebTheme;
  postTheme?: PostTheme;
}

const PostContent = ({ node, children, webTheme, postTheme }: Props) => {
  // PostContent is only meaningful inside PostPageProvider (the /p/$slug route).
  // Render nothing if a stray `postContent` node appears on another page type.
  // Do not add more hooks to this component — the early return below would make
  // them conditional; put any new hooks inside PostContentInner instead.
  const postPageContext = usePostPageContext();
  const postDataContext = useContext(PostDataContext);

  if (!postPageContext || !postDataContext) {
    return null;
  }

  return (
    <PostContentInner node={node} postPageContext={postPageContext} webTheme={webTheme} postTheme={postTheme}>
      {children}
    </PostContentInner>
  );
};

interface InnerProps extends Props {
  postPageContext: PostPageContextType;
}

const PostContentInner = ({
  node,
  children,
  postPageContext,
  webTheme: webThemeOverride,
  postTheme: postThemeOverride,
}: InnerProps) => {
  const { site, subscription } = useRouteLoaderData('root') as RootLoaderData;
  const { post, postMergeTags, paywall, onlyRenderComments, previewSubscription, postMetaInformation } =
    usePostDataContext();
  const isPostMetaFetched = !!postMetaInformation?._fetched;

  const { t } = useTranslation();
  const { isLoggedIn } = useOutletContext<OutletContext>();
  const { handleOpenModal } = useGlobalModals();

  const {
    isGatedContentDisabled,
    showSubscriberBreak,
    showPaywallBreak,
    showPaywallAndHidePostContent,
    showMeteredUsageBanner,
    showTextToSpeechBlock,
    paywallUrl,
    utmParams,
  } = postPageContext;
  const postTheme = postThemeOverride ?? post.post_theme;
  const webTheme = webThemeOverride ?? {
    ...site.publication.web_theme,
    text_on_background_color: node.attrs.color || site.publication.web_theme.text_on_background_color,
  };

  const hasMeteredPaywallBreak = showPaywallBreak && !!paywall && isMeteredPaywall(paywall);
  const paywallBreakSentinelRef = useRef<HTMLDivElement>(null);
  const [isPaywallBreakInView, setIsPaywallBreakInView] = useState(false);

  useEffect(() => {
    if (!hasMeteredPaywallBreak || !paywallBreakSentinelRef.current) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsPaywallBreakInView(true);
    });

    observer.observe(paywallBreakSentinelRef.current);
    return () => observer.disconnect();
  }, [hasMeteredPaywallBreak]);

  const loginPath = useMemo(() => {
    return `/login?postSlug=${encodeURIComponent(`/p/${post.slug}`)}`;
  }, [post.slug]);

  useRenderTiktokEmbeds(post.slug, isGatedContentDisabled && !onlyRenderComments);
  useRenderTwitterEmbeds(post.slug, isGatedContentDisabled && !onlyRenderComments);
  useRenderBlueskyEmbeds(post.slug, isGatedContentDisabled && !onlyRenderComments);
  useRenderStocktwitsEmbeds(post.slug, isGatedContentDisabled && !onlyRenderComments);

  const currentSubscription = subscription || previewSubscription;

  const processorOptions: ProcessorFunctionOptions = useMemo(() => {
    return {
      subscriberBreakElementData: {
        title: post?.email_capture_title,
        message: post?.email_capture_message,
        cta: post?.email_capture_cta,
        is_publication_private: !!site?.publication?.private,
        site_settings: {
          terms_of_service_enabled: site?.viewable_site_version?.settings?.terms_of_service_enabled,
          terms_of_service: site?.viewable_site_version?.settings?.terms_of_service,
        },
      },
      hasSubscription: !!currentSubscription,
      postMergeTags,
      subscriberId: currentSubscription?.subscriber_id,
      subscriberEmail: currentSubscription?.email,
      postTiptapState: postMetaInformation?.post_tiptap_state,
    };
  }, [site, post, currentSubscription, postMergeTags, postMetaInformation?.post_tiptap_state]);

  const hasCustomMergeTags = processorOptions?.postMergeTags && Object.keys(processorOptions.postMergeTags).length > 0;
  const hasPollSubscriberToReplace = site.publication.has_polls && processorOptions?.subscriberId;
  const needsMergeTagProcessing = hasCustomMergeTags || hasPollSubscriberToReplace;

  const processorList: ProcessorFunction[] = [
    processors.processData,
    ...(showSubscriberBreak ? [processors.processSubscriberGate] : []),
    ...(needsMergeTagProcessing ? [processors.processMergeTags] : []),
  ];

  const pollInteractionValue = useMemo<PollInteractionContextType>(
    () => ({
      submitVote: async (params) => {
        const response = await fetch(`/polls/${params.pollId}/response`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        });
        if (!response.ok) throw new Error('Vote failed');
        return response.json();
      },
    }),
    [],
  );

  const podcastEpisodeEmbedInteractionValue = useMemo<PodcastEpisodeEmbedInteractionContextType>(() => {
    const onPlayAttempt = !isLoggedIn ? () => handleOpenModal('login', { preventScrollReset: true }) : undefined;
    const getIsPlaybackLocked = (audioUrl?: string) => isLoggedIn && !audioUrl;
    const getTooltipText = (audioUrl?: string) =>
      getIsPlaybackLocked(audioUrl) ? t('podcasts.episode.audio_player.locked_tooltip') : undefined;

    return {
      onPlayAttempt,
      getTooltipText,
      getIsPlaybackLocked,
    };
  }, [handleOpenModal, isLoggedIn, t]);

  if (onlyRenderComments) {
    return null;
  }

  const processedElement = process(node, processorList, processorOptions);

  return (
    <>
      {showTextToSpeechBlock && 'text_to_speech_config' in post && post?.text_to_speech_config ? (
        <PostAudio textToSpeechConfig={post?.text_to_speech_config} />
      ) : null}
      <PostContentPaywall
        paywall={paywall}
        showPaywallAndHidePostContent={showPaywallAndHidePostContent}
        showMeteredUsageBanner={showMeteredUsageBanner}
        isPaywallBreakInView={isPaywallBreakInView}
        paywallUrl={paywallUrl}
      />
      {showPaywallAndHidePostContent && !isPostMetaFetched && <PostContentSkeleton />}
      {!showPaywallAndHidePostContent && (
        <PollInteractionContext.Provider value={pollInteractionValue}>
          <PodcastEpisodeEmbedInteractionContext.Provider value={podcastEpisodeEmbedInteractionValue}>
            <PostContentElement
              id="content-blocks"
              webTheme={webTheme}
              postTheme={postTheme as PostTheme}
              element={processedElement}
              isLive
              isWeb
              translations={{
                'login.already_a_subscriber': t('login.already_a_subscriber'),
                'shared.cta.sign_in': t('shared.cta.sign_in'),
                'login.not_now': t('login.not_now'),
                'shared.actions.submitting': t('shared.actions.submitting'),
                'subscribe.email_placeholder': t('subscribe.email_placeholder'),
                'shared.cta.subscribe': t('shared.cta.subscribe'),
                'shared.cta.login': t('shared.cta.login'),
                'shared.cta.or': t('shared.cta.or'),
                'shared.cta.to_participate': t('shared.cta.to_participate'),
                'shared.poll.subscribe_text': t('shared.poll.subscribe_text'),
                'shared.image.missing_image': t('shared.image.missing_image'),
                'shared.cta.gdpr_signup_consent': t('shared.cta.gdpr_signup_consent'),
                'footer.terms_of_use': t('footer.terms_of_use'),
                'footer.privacy_policy': t('footer.privacy_policy'),
                'shared.cta.and': t('shared.cta.and'),
                'shared.cta.sign_up': t('shared.cta.sign_up'),
                'meta.titles.tos': t('meta.titles.tos'),
                'polls.inline.vote_recorded': t('polls.inline.vote_recorded'),
                'polls.inline.trivia_correct': t('polls.inline.trivia_correct'),
                'polls.inline.trivia_incorrect': t('polls.inline.trivia_incorrect'),
                'polls.inline.error': t('polls.inline.error'),
                'polls.inline.continue': t('polls.inline.continue'),
                'polls.inline.share_feedback': t('polls.inline.share_feedback'),
              }}
              rewriteImageSrc={rewriteSrc}
              loginPath={loginPath}
              subscribePath="/subscribe"
              homePath="/"
              upgradePath="/upgrade"
              showSubscriberBreak={showSubscriberBreak}
              showPaywallBreak={showPaywallBreak}
              ChildDecoratorComponent={ChildDecoratorComponent}
              isIndexingDisabled={site?.viewable_site_version?.metadata?.noindex_enabled === true} // replaces nofollow|noreferrer|noopener in the value of anchors' rel attribute
              utmParams={utmParams}
              isSubscriberAuthenticated={isLoggedIn}
            >
              {children}
            </PostContentElement>
          </PodcastEpisodeEmbedInteractionContext.Provider>
        </PollInteractionContext.Provider>
      )}
      {hasMeteredPaywallBreak && <div ref={paywallBreakSentinelRef} />}
    </>
  );
};

export { PostContent };
